import { useCallback, useRef, useState } from 'react'

interface UseScrollHighlightOptions {
  /** Element ID to scroll to */
  elementId: string
  /** Duration of highlight effect in ms. Defaults to 1200. */
  highlightDuration?: number
  /** Delay before highlight starts when scrolling. Defaults to 650. */
  scrollDelay?: number
  /** Scroll alignment. Defaults to 'center'. */
  scrollBlock?: ScrollLogicalPosition
}

interface UseScrollHighlightReturn {
  /** Whether the element is currently highlighted */
  isHighlighted: boolean
  /** Callback to scroll to element and trigger highlight */
  scrollToElement: () => void
}

/**
 * Hook for scrolling to an element with a temporary highlight effect
 *
 * Handles:
 * - Smooth scrolling to element if not in view
 * - Delayed highlight after scroll animation
 * - Auto-removal of highlight after duration
 * - Cleanup of timeouts on unmount
 *
 * @example
 * ```tsx
 * const { isHighlighted, scrollToElement } = useScrollHighlight({
 *   elementId: 'files-section',
 * })
 *
 * return (
 *   <>
 *     <button onClick={scrollToElement}>View Files</button>
 *     <div
 *       id="files-section"
 *       className={isHighlighted ? 'ring-2 ring-accent-400' : ''}
 *     >
 *       ...
 *     </div>
 *   </>
 * )
 * ```
 */
export function useScrollHighlight({
  elementId,
  highlightDuration = 1200,
  scrollDelay = 650,
  scrollBlock = 'center',
}: UseScrollHighlightOptions): UseScrollHighlightReturn {
  const [isHighlighted, setIsHighlighted] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const scrollToElement = useCallback(() => {
    const el = document.getElementById(elementId)
    if (!el) return

    const rect = el.getBoundingClientRect()
    const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight

    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Scroll if not in view
    if (!isInView) {
      el.scrollIntoView({ behavior: 'smooth', block: scrollBlock })
    }

    // Delay highlight if scrolling, immediate if already in view
    const glowDelay = isInView ? 0 : scrollDelay

    timeoutRef.current = setTimeout(() => {
      setIsHighlighted(true)
      timeoutRef.current = setTimeout(() => {
        setIsHighlighted(false)
      }, highlightDuration)
    }, glowDelay)
  }, [elementId, highlightDuration, scrollDelay, scrollBlock])

  return { isHighlighted, scrollToElement }
}
